/**
 * useNearbyPois — 카카오 카테고리 검색으로 현장 주변 POI(지하철·학교·마트·병원)를 조회.
 *
 * 목업 `.poi` 리스트(아이콘 + 제목/부제 + 우측 거리)에 쓰일 데이터를 만든다.
 * 카테고리별로 가장 가까운 1건(sort:'distance', radius 3000m)만 취한다.
 *
 * 정책/주의:
 *   - 카카오 SDK(services) 미로드·좌표 없음·검색 실패 시 항상 빈 배열로 폴백 (페이지 정상 유지).
 *   - POI 는 표시만 한다. 외부 링크 금지.
 *   - 아이콘은 카테고리별 inline SVG (이모지 미사용).
 */

import React, { useEffect, useState } from 'react';
import type { PoiItem } from '../../design-system/components/section';
import { whenKakaoMapsReady } from '../../utils/kakaoMapsReady';

// ─────────────────────────────────────────────
// 카테고리별 아이콘 (목업 detail_mock `.poi .ic svg` 기준)
// ─────────────────────────────────────────────
const SubwayIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <rect x="4" y="3" width="16" height="16" rx="2" />
    <path d="M4 11h16M8 19l-2 3M16 19l2 3" />
  </svg>
);
const SchoolIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M22 10L12 5 2 10l10 5 10-5zM6 12v5c0 1 3 2 6 2s6-1 6-2v-5" />
  </svg>
);
const MartIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 22V12h6v10" />
  </svg>
);
const HospitalIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 10v6M9 13h6" />
  </svg>
);

// ─────────────────────────────────────────────
// 조회 카테고리 (가까운 1건씩). 카카오 카테고리 그룹 코드.
// ─────────────────────────────────────────────
export interface PoiCategory {
  code: string; // 카카오 category_group_code
  icon: React.ReactNode;
}

export const POI_CATEGORIES: PoiCategory[] = [
  { code: 'SW8', icon: <SubwayIcon /> }, // 지하철역
  { code: 'SC4', icon: <SchoolIcon /> }, // 학교
  { code: 'MT1', icon: <MartIcon /> }, // 대형마트
  { code: 'HP8', icon: <HospitalIcon /> }, // 병원
];

/**
 * 거리 표기: m<1000 → "Nm", 아니면 "N.Nkm".
 * 값이 없거나 비정상이면 undefined (거리 미표시).
 */
export function formatPoiDistance(meters: number): string | undefined {
  if (!Number.isFinite(meters) || meters < 0) return undefined;
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

// 카카오 place 결과의 최소 형태 (문서상 필드).
export interface KakaoPlace {
  place_name?: string;
  road_address_name?: string;
  address_name?: string;
  distance?: string; // meters, 문자열
}

/**
 * 카카오 place 1건 + 카테고리 → PoiItem 변환 (순수 함수).
 * 부제는 도로명 주소 우선, 없으면 지번 주소.
 */
export function poiFromPlace(category: PoiCategory, place: KakaoPlace): PoiItem {
  return {
    icon: category.icon,
    title: place.place_name ?? '',
    sub: place.road_address_name || place.address_name || undefined,
    distance: formatPoiDistance(Number(place.distance)),
  };
}

/**
 * 현장 좌표로 주변 POI 를 조회하는 훅.
 * @returns items: 성공한 카테고리만 담긴 PoiItem 배열, loading: 조회 중 여부.
 */
export function useNearbyPois(
  lat?: number | null,
  lng?: number | null,
): { items: PoiItem[]; loading: boolean } {
  const [items, setItems] = useState<PoiItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lat == null || lng == null) {
      setItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    // SDK(autoload=false) 초기화 완료를 기다린 뒤 조회 — 직접 진입 시 경쟁 조건 방지 (2026-09-21)
    whenKakaoMapsReady().then((kakao) => {
      if (cancelled) return;
      if (!kakao?.maps?.services) {
        setItems([]);
        setLoading(false);
        return;
      }

      const services = kakao.maps.services;
      const places = new services.Places();
      const location = new kakao.maps.LatLng(lat, lng);
      const results: (PoiItem | null)[] = new Array(POI_CATEGORIES.length).fill(null);
      let remaining = POI_CATEGORIES.length;

      const done = () => {
        remaining -= 1;
        if (remaining === 0 && !cancelled) {
          setItems(results.filter((p): p is PoiItem => p != null));
          setLoading(false);
        }
      };

      POI_CATEGORIES.forEach((cat, idx) => {
        try {
          places.categorySearch(
            cat.code,
            (data: KakaoPlace[], status: string) => {
              if (cancelled) return;
              if (status === services.Status.OK && data && data.length > 0) {
                results[idx] = poiFromPlace(cat, data[0]);
              }
              done();
            },
            { location, radius: 3000, sort: 'distance' },
          );
        } catch {
          // 개별 카테고리 실패는 무시하고 나머지로 폴백
          done();
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  return { items, loading };
}

export default useNearbyPois;
